// Varisankhya contract shared by the API and the web app (pilot slice 7, ADR-0032). Amounts are whole rupees.
// Unpaid varisankhya is information only: it never blocks a certificate, a burial record or madrasa admission.
import type { Role } from './auth';
import type { NoticeSkip, ReceiptNotice } from './messaging';
import type { RatePlan } from './register';

/** Roles that set plans and funds, raise dues and cancel receipts. */
export const FINANCE_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'SECRETARY', 'TREASURER'];

/** Roles that may collect money and issue a receipt. Collectors only for houses in their wards. */
export const COLLECT_ROLES: readonly Role[] = [...FINANCE_ROLES, 'COLLECTOR'];

export const RECEIPT_METHODS = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE'] as const;
export type ReceiptMethod = (typeof RECEIPT_METHODS)[number];

export const RECEIPT_STATUSES = ['ISSUED', 'CANCELLED'] as const;
export type ReceiptStatus = (typeof RECEIPT_STATUSES)[number];

export const FUND_KINDS = ['DUES', 'DONATION'] as const;
export type FundKind = (typeof FUND_KINDS)[number];

/** The one dues fund; every other fund is a donation fund. */
export const DUES_FUND = 'VARISANKHYA';

export const MONEY_LIMITS = { amount: 10_000_000, monthly: 100_000, monthsAhead: 24, note: 300, reference: 80, payer: 120, reason: 300 } as const;

/** "2026-11" for a period; the API stores the first day of the month. */
export const PERIOD = /^\d{4}-(0[1-9]|1[0-2])$/;

export interface RatePlanRow {
  code: RatePlan;
  nameEn: string;
  nameMl: string | null;
  monthlyInr: number;
  /** Active houses on this plan. */
  houses: number;
  version: number;
}

export interface RatePlanPatch {
  nameEn?: string;
  nameMl?: string | null;
  monthlyInr?: number;
  /** Also move houses on this plan that still pay the old amount to the new one (future dues only). */
  applyToHouses?: boolean;
  version: number;
}

export interface Fund {
  id: string;
  code: string;
  kind: FundKind;
  nameEn: string;
  nameMl: string | null;
  active: boolean;
  version: number;
}

export interface FundInput {
  code: string;
  nameEn: string;
  nameMl?: string | null;
}

export interface DueRow {
  id: string;
  /** "2026-11" */
  period: string;
  amountInr: number;
  paidInr: number;
}

export interface HouseAccount {
  houseId: string;
  monthlyRateInr: number;
  /** Unpaid amount of months before the current one. */
  arrearsInr: number;
  arrearsMonths: number;
  /** Everything unpaid, including the current month. */
  outstandingInr: number;
  /** "2026-11", Indian time. */
  currentPeriod: string;
  /** Unpaid dues, oldest first, then the last paid ones (at most 24 rows). */
  dues: DueRow[];
  receipts: ReceiptRow[];
}

export interface ReceiptInput {
  fundCode: string;
  houseId?: string | null;
  /** For a donor outside the register; required when there is no house. */
  payerName?: string | null;
  amountInr: number;
  method: ReceiptMethod;
  reference?: string | null;
  /** "2026-11-04"; not in the future. */
  receivedOn: string;
  note?: string | null;
  /** Varisankhya only: raise dues for this many months after the last one, and pay them too. */
  monthsAhead?: number;
  /** Send the family the receipt on WhatsApp (SMS if WhatsApp fails). Default true. */
  notify?: boolean;
}

export interface ReceiptRow {
  id: string;
  receiptNo: string;
  fund: { code: string; nameEn: string; nameMl: string | null };
  house: { id: string; houseNo: string; nameEn: string; nameMl: string | null } | null;
  payerName: string | null;
  amountInr: number;
  method: ReceiptMethod;
  reference: string | null;
  receivedOn: string;
  note: string | null;
  collectedBy: string;
  createdAt: string;
  status: ReceiptStatus;
  cancelReason: string | null;
  cancelledAt: string | null;
  /** Months a varisankhya receipt paid, "2026-09", oldest first. */
  periods: string[];
  /** The WhatsApp/SMS message to the family about this receipt (slice 8), if one was queued. */
  notice: ReceiptNotice | null;
  /** On the issue answer only: why no message was queued. */
  noticeSkipped?: NoticeSkip;
  version: number;
}

export interface ReceiptList {
  rows: ReceiptRow[];
  total: number;
  /** Sum of ISSUED receipts matching the filters (cancelled ones never count). */
  issuedInr: number;
  page: number;
  size: number;
}

export const RECEIPT_FILTERS = ['fund', 'status', 'ward', 'from', 'to', 'q', 'page', 'size'] as const;

export interface DuesMonth {
  /** "2026-11" */
  period: string;
  /** Active houses with a monthly amount above zero (the ones a raise would cover). */
  eligibleHouses: number;
  /** Houses that already have a due for this month. */
  raisedHouses: number;
  amountInr: number;
  paidInr: number;
}

export interface RaiseResult {
  period: string;
  created: number;
  amountInr: number;
}

/** "2026-11" for a date, in Indian time. */
export function periodOf(d: Date): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit' }).formatToParts(d);
  return `${p.find((x) => x.type === 'year')!.value}-${p.find((x) => x.type === 'month')!.value}`;
}

/** The month n months after a period ("2026-11", 2 → "2027-01"). */
export function addMonths(period: string, n: number): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const i = y * 12 + (m - 1) + n;
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
}
